<script setup>
import { computed, defineOptions, useAttrs } from 'vue';

import ImageChip from 'next/message/chips/Image.vue';
import VideoChip from 'next/message/chips/Video.vue';
import AudioChip from 'next/message/chips/Audio.vue';
import FileChip from 'next/message/chips/File.vue';
import Icon from 'next/icon/Icon.vue';
import { useMessageContext } from '../provider.js';

import { ATTACHMENT_TYPES } from '../constants';

/**
 * @typedef {Object} Attachment
 * @property {number} id - Unique identifier for the attachment
 * @property {number} messageId - ID of the associated message
 * @property {'image'|'audio'|'video'|'file'|'location'|'fallback'|'share'|'story_mention'|'contact'|'ig_reel'} fileType - Type of the attachment (file or image)
 * @property {number} accountId - ID of the associated account
 * @property {string|null} extension - File extension
 * @property {string} dataUrl - URL to access the full attachment data
 * @property {string} thumbUrl - URL to access the thumbnail version
 * @property {number} fileSize - Size of the file in bytes
 * @property {number|null} width - Width of the image if applicable
 * @property {number|null} height - Height of the image if applicable
 */
const props = defineProps({
  attachments: {
    type: Array,
    default: () => [],
  },
});

defineOptions({
  inheritAttrs: false,
});

const attrs = useAttrs();
const { orientation } = useMessageContext();

const classToApply = computed(() => {
  const baseClasses = [attrs.class, 'flex', 'flex-wrap'];

  if (orientation.value === 'right') {
    baseClasses.push('justify-end');
  }

  return baseClasses;
});

const allAttachments = computed(() => {
  return Array.isArray(props.attachments) ? props.attachments : [];
});

const mediaAttachments = computed(() => {
  const allowedTypes = [ATTACHMENT_TYPES.IMAGE, ATTACHMENT_TYPES.VIDEO];
  const mediaTypes = allAttachments.value.filter(attachment =>
    allowedTypes.includes(attachment.fileType)
  );

  return mediaTypes.sort(
    (a, b) =>
      allowedTypes.indexOf(a.fileType) - allowedTypes.indexOf(b.fileType)
  );
});

const recordings = computed(() => {
  return allAttachments.value.filter(
    attachment => attachment.fileType === ATTACHMENT_TYPES.AUDIO
  );
});

const files = computed(() => {
  return allAttachments.value.filter(
    attachment => attachment.fileType === ATTACHMENT_TYPES.FILE
  );
});

// A `fallback` is a shared photo/post/ad/story Facebook can't deliver as a
// native attachment — its link arrives as the attachment's dataUrl. There was
// no renderer for it, so these messages showed as blank bubbles. Render a
// clickable link; if the url was never captured (legacy empty rows) show a
// muted label so the agent still knows the customer shared something.
const fallbacks = computed(() => {
  return allAttachments.value.filter(
    attachment => attachment.fileType === ATTACHMENT_TYPES.FALLBACK
  );
});

// Guard the href against javascript:/data: schemes before rendering a link
// (defense-in-depth; Meta only sends http(s) photo/post urls). Unsafe or
// missing urls fall through to the muted, non-clickable label below.
const isSafeHttpUrl = url => {
  if (!url) return false;
  try {
    return ['http:', 'https:'].includes(new URL(url).protocol);
  } catch {
    return false;
  }
};
</script>

<template>
  <div v-if="mediaAttachments.length" :class="classToApply">
    <template v-for="attachment in mediaAttachments" :key="attachment.id">
      <ImageChip
        v-if="attachment.fileType === ATTACHMENT_TYPES.IMAGE"
        :attachment="attachment"
      />
      <VideoChip
        v-else-if="attachment.fileType === ATTACHMENT_TYPES.VIDEO"
        :attachment="attachment"
      />
    </template>
  </div>
  <div v-if="recordings.length" :class="classToApply">
    <div v-for="attachment in recordings" :key="attachment.id">
      <AudioChip
        class="bg-n-alpha-3 dark:bg-n-alpha-2 text-n-slate-12"
        :attachment="attachment"
      />
    </div>
  </div>
  <div v-if="files.length" :class="classToApply">
    <FileChip
      v-for="attachment in files"
      :key="attachment.id"
      :attachment="attachment"
    />
  </div>
  <div v-if="fallbacks.length" :class="classToApply">
    <template v-for="attachment in fallbacks" :key="attachment.id">
      <a
        v-if="isSafeHttpUrl(attachment.dataUrl)"
        class="flex items-center h-9 gap-2 px-2 overflow-hidden text-sm no-underline border rounded-lg bg-n-alpha-white border-n-container text-n-slate-12 hover:text-n-slate-11"
        :href="attachment.dataUrl"
        rel="noreferrer noopener nofollow"
        target="_blank"
      >
        <Icon icon="i-lucide-link" class="flex-shrink-0" />
        <span class="min-w-0 truncate max-w-48">
          {{ attachment.fallbackTitle || 'محتوى مُشارك من فيسبوك' }}
        </span>
      </a>
      <div
        v-else
        class="flex items-center h-9 gap-2 px-2 overflow-hidden text-sm border rounded-lg bg-n-alpha-white border-n-container text-n-slate-11"
      >
        <Icon icon="i-lucide-link" class="flex-shrink-0" />
        <span class="min-w-0 truncate max-w-48">
          {{ attachment.fallbackTitle || 'محتوى مُشارك (الرابط غير متاح)' }}
        </span>
      </div>
    </template>
  </div>
</template>
